import React from "react";
import DetalleCard from "~business/Cards/DetalleCard";
import { useDispatch, useSelector } from "react-redux";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ItemInputCard from "~business/Cards/ItemInputCard";
import ItemDateCard from "~business/Cards/ItemDateCard";
import getProvincias from "~styled/vehiculoProvinciaSelector/getProvincias";
import MultipleItemSelectorCard, {
  ListItem,
} from "~business/Cards/MultipleItemSelectorCard";
import type { FieldChangeEvent } from "~business/Cards/types";

import {
  setFecha,
  setHora,
  setDireccion,
  setLocalidad,
  setProvincia,
  setCodigoPostal,
  setSubmitting,
  fetchLocalidades,
  fetchCodigosPostales,
} from "../actions";
import {
  CampoFecha,
  CampoProvincia,
  CampoSiniestro,
  OpcionSiniestro,
} from "../../../types";

interface SliceFechaLugar {
  fecha: CampoFecha;
  hora: CampoSiniestro;
  direccion: CampoSiniestro;
  localidad: CampoSiniestro;
  provincia: CampoProvincia;
  codigoPostal: CampoSiniestro;
  localidades: OpcionSiniestro[];
  codigosPostales: OpcionSiniestro[];
}

const useReduxSelector = () =>
  useSelector<any, SliceFechaLugar>((state) => ({
    fecha: state.cargaSiniestroReducer.fechaSelected,
    hora: state.cargaSiniestroReducer.horaSelected,
    direccion: state.cargaSiniestroReducer.direccionSelected,
    localidad: state.cargaSiniestroReducer.localidadSelected,
    provincia: state.cargaSiniestroReducer.provinciaSelected,
    codigoPostal: state.cargaSiniestroReducer.codigoPostalSelected,
    localidades: state.cargaSiniestroReducer.localidades,
    codigosPostales: state.cargaSiniestroReducer.codigosPostales,
  }));

const title = "Fecha y Lugar";

interface FechaLugarCardProps {
  isLoading?: boolean;
}

const FechaLugarCard = ({ isLoading }: FechaLugarCardProps) => {
  const dispatch = useDispatch();

  const {
    fecha,
    hora,
    direccion,
    localidad,
    provincia,
    codigoPostal,
    localidades,
    codigosPostales,
  } = useReduxSelector();

  let timer: ReturnType<typeof setTimeout> | undefined;

  const handleChangeFecha = (fecha: FieldChangeEvent<string | Date | null>) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      dispatch(setFecha(fecha));
    }, 200);
  };

  const handleChangeHora = (hora: FieldChangeEvent<string | null>) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      dispatch(setHora(hora));
    }, 200);
  };

  const handleChangeDireccion = (
    direccion: FieldChangeEvent<string | null>
  ) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      dispatch(setDireccion(direccion));
    }, 200);
  };

  const handleChangeLocalidad = (localidad: ListItem) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      dispatch(setLocalidad(localidad));
      if (provincia.searchId) {
        dispatch(
          fetchCodigosPostales(provincia.searchId, localidad.value ?? "")
        );
      }
    }, 200);
  };

  const handleChangeProvincia = (provincia: ListItem) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      dispatch(setProvincia(provincia));
      if (provincia.value) dispatch(fetchLocalidades(provincia.value));
    }, 200);
  };

  const handleChangeCodigoPostal = (codigoPostal: ListItem) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      dispatch(setCodigoPostal(codigoPostal));
    }, 200);
  };

  const handleBlur = () => {
    dispatch(setSubmitting(false));
  };

  const provincias = getProvincias().map((provincia) => ({
    label: provincia.label,
    value: String(provincia.value),
  }));

  return (
    <>
      <DetalleCard title={title} IconHeader={ScheduleIcon}>
        <ItemDateCard
          id={"fecha"}
          isLoading={isLoading}
          label={"Fecha"}
          value={fecha.value}
          onChange={handleChangeFecha}
          onBlur={handleBlur}
          focus={fecha.focus}
          maxDate={new Date()}
          minDate={new Date("2000-01-01")}
          onValidate={(value) =>
            !!value &&
            new Date(value) >= new Date("2000-01-01") &&
            new Date(value) <= new Date()
          }
          invalidDateMessage={"Ingresá una fecha válida"}
          required
        />
        <ItemInputCard
          id={"hora"}
          isLoading={isLoading}
          label={"Hora"}
          value={hora.value}
          onChange={handleChangeHora}
          onBlur={handleBlur}
          focus={hora.focus}
          placeholder={"HH:MM"}
          required
          maxLength={5}
        />
        <ItemInputCard
          id={"direccion"}
          isLoading={isLoading}
          label={"Calle, altura e intersección"}
          value={direccion.value}
          onChange={handleChangeDireccion}
          onBlur={handleBlur}
          focus={direccion.focus}
          required
          multiline
        />

        <MultipleItemSelectorCard
          id={"provincia"}
          isLoading={isLoading}
          label={"Provincia"}
          value={provincia.value}
          onChange={handleChangeProvincia}
          placeholder={"Seleccionar"}
          onBlur={handleBlur}
          focus={provincia.focus}
          tooltipContent={"Seleccionar Provincia"}
          items={provincias}
        />

        <MultipleItemSelectorCard
          id={"localidad"}
          isLoading={isLoading}
          label={"Localidad"}
          value={localidad.value}
          onChange={handleChangeLocalidad}
          placeholder={"Seleccionar"}
          onBlur={handleBlur}
          focus={localidad.focus}
          tooltipContent={"Seleccionar Localidad"}
          items={localidades}
        />

        <MultipleItemSelectorCard
          id={"codigoPostal"}
          isLoading={isLoading}
          label={"Código Postal"}
          value={codigoPostal.value}
          onChange={handleChangeCodigoPostal}
          placeholder={"Seleccionar"}
          onBlur={handleBlur}
          focus={codigoPostal.focus}
          tooltipContent={"Seleccionar Código Postal"}
          items={codigosPostales}
        />
      </DetalleCard>
    </>
  );
};

export default FechaLugarCard;
